/**
 * Handles all 3rd party functionality
 */
import Logger from '../helpers/logger';

const log = new Logger();
const dataLayer = window.dataLayer;

export default ['$http', function ThirdPartyService($http) {
  /**
   * Loads Optimizely/YM tracking pixels
   */
  this.loadTrackers = () => {
    const el = document.querySelector('#final_analytics');
    const urls = [
      '//ad.yieldmanager.com/pixel?id=1239899&t=2',
      '//ad.yieldmanager.com/pixel?id=1239919&t=2',
      '//www.googleadservices.com/pagead/conversion/1069322756/?value=0&label=G45LCMLu6wYQhKTy_QM&guid=ON&script=0'
    ];

    urls.forEach((url) => {
      const img = document.createElement('img');
      img.src = url;
      img.width = 0;
      img.height = 0;
      img.alt = '';

      el.parentNode.insertBefore(img, el);
    });

    // After conversion pixels load, sends tracking data to Optimizely for demo conversion
    window.optimizely = window.optimizely || [];
    window.optimizely.push({ type: 'event', eventName: 'trial_signup' }); // Optimizely X

    log.debug({
      category: 'thirdParty',
      message: 'loadTrackers'
    });
  };

  /**
   * Send GA data
   *
   * @param response
   */
  this.sendAnalytics = (response) => {
    const id = (response.version === 'v1') ? response.data.d.OrderID : response.tenantId;

    dataLayer.push({
      event: 'demoTransaction',
      trialTransactionId: id
    });

    log.debug({
      category: 'thirdParty',
      message: 'sendAnalytics'
    });
  };

  /**
   * Build out a Salesforce object for the V2 create endpoint
   *
   * @param response
   * @returns {*}
   */
  this.buildSalesforceObject = (response) => {
    const fields = {
      emailAddress: 'Email',
      firstName: 'FirstName',
      lastName: 'LastName',
      tenantId: 'Tenant_ID__c',
      phoneNumber: 'Phone',
      country: 'Country',
      SMSOptIn: 'SMS_Opt_in__c',
      source: ['Marketing_1__c', 'GA_Source__c'],
      medium: ['Marketing_2__c', 'GA_Medium__c'],
      campaign: ['Marketing_3__c', 'GA_Campaign__c'],
      keyword: ['Marketing_4__c', 'GA_Keyword__c'],
      landingpage: ['LandingPage__c', 'GA_LandingPage__c'],
      referrer: 'Referrer__c',
      storeUrl: ['Store_URL__c', 'Demo_URL__c'],
      gaClientID: 'gaClientID__c',
      leadDescription: 'Lead_Source_Description__c',
      fullStorySessionUrl: 'FullStoryTrialSessionURL__c',
      deviceType: 'Device_Type__c',
      Geo_Continent__c: 'Geo_Continent__c',
      Geo_Country__c: 'Geo_Country__c',
      Geo_State__c: 'Geo_State__c',
      Geo_City__c: 'Geo_City__c',
      Geo_Postal_Code__c: 'Geo_Postal_Code__c',
      Geo_Time_Zone__c: 'Geo_Time_Zone__c',
      Geo_Latitude__c: 'Geo_Latitude__c',
      Geo_Longitude__c: 'Geo_Longitude__c',
      Geo_Ip__c: 'Geo_Ip__c',
      friendBuyReferralCode: 'FriendBuy_Referral_Code__c',
      influencerReferralCode: 'Referral_Code__c',
      SocialSignUp__c: 'SocialSignUp__c',
      SocialSignUpNetwork__c: 'SocialSignUpNetwork__c',
      Pricing_Plans: 'Pricing_Plans__c'
    };

    const sfData = {};

    Object.keys(response).forEach((key) => {
      if (fields[key] === undefined) {
        return;
      }

      if (Array.isArray(fields[key])) {
        fields[key].forEach((k) => {
          sfData[k] = response[key];
        });
        return;
      }

      sfData[fields[key]] = response[key];
    });

    log.debug({
      category: 'thirdParty',
      message: 'buildSalesforceObject',
      data: sfData
    });

    return sfData;
  };

  /**
   * Send V1 Lead Salesforce
   *
   * @param response
   * @returns {*}
   */
  this.sendV1LeadSalesforce = (response) => {
    log.debug({
      category: 'thirdParty',
      message: 'sendV1LeadSalesforce'
    });

    return $http.post(process.env.TRIALS.V1_SF_LEAD_URL, response);
  };

  /**
   * Send V2 info to warehouse
   *
   * @param response
   * @returns Promise
   */
  this.sendV2Warehouse = (response) => {

    log.debug({
      category: 'thirdParty',
      message: 'sendV2Warehouse'
    });

    return $http.post(process.env.TRIALS.V2_WAREHOUSE_URL, response);
  };

  /**
   * Send V1 info to growsumo
   *
   * @param response
   * @returns Promise
   */
  this.sendV1Growsumo = (response) => {
    log.debug({
      category: 'thirdParty',
      message: 'sendV1Growsumo'
    });

    try {
      if (!window.growsumo) return;
      window.growsumo.data.name = response.firstName;
      window.growsumo.data.email = response.emailAddress;
      window.growsumo.data.customer_key = response.customerId.toString();
      window.growsumo.createSignup();
    } catch (e) {
      log.exception(e);
    }
  };

  /**
   * Send V2 info to growsumo
   *
   * @param response
   * @returns Promise
   */
  this.sendV2Growsumo = (response) => {
    log.debug({
      category: 'thirdParty',
      message: 'sendV2Growsumo'
    });

    try {
      if (!window.growsumo) return;
      window.growsumo.data.name = response.firstName;
      window.growsumo.data.email = response.emailAddress;
      window.growsumo.data.customer_key = response.tenantId;
      window.growsumo.createSignup();
    } catch (e) {
      log.exception(e);
    }
  };

  /**
   * Merges lots of separate data into one response data object that gets passed around
   *
   * @param response
   * @param version
   * @param data
   * @param storeName
   * @param storeUrl
   * @param tenantId
   * @param createdOn
   * @param optimizely
   * @return {*}
   */
  this.mergeResponseObject = (response, version, data, storeName, storeUrl, tenantId, createdOn, optimizely) => {
    const leadTimeSent = Date.now() || 'error';

    if (version === 'v1') {
      const customerId = response.data.d.CustomerID;
      storeUrl = response.data.d.DomainName;

      Object.assign(
        response,
        data,
        { customerId },
        { leadTimeSent },
        { optimizely },
        { storeUrl },
        { version }
      );

      delete response.password;
      delete response.config;
      delete response.headers;

      return response;
    }

    Object.assign(
      response,
      data,
      { createdOn },
      { leadTimeSent },
      { optimizely },
      { storeName },
      { storeUrl },
      { tenantId },
      { version }
    );

    delete response.password;
    delete response.config;
    delete response.headers;

    log.debug({
      category: 'thirdParty',
      message: 'mergeResponseObject',
      data: response
    });

    return response;
  };
}];

